import React, { useState } from 'react';
import { TextField, styled, InputAdornment, TextFieldProps, IconButton, useTheme, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';
import EyeClosed from '../assets/eye_closed.svg?react';
import EyeNormal from '../assets/eye_normal.svg?react';

interface CommonTextFieldProps extends Omit<TextFieldProps, 'variant'> {
    name: string;
    icon?: string;
    error?: boolean;
    helperText?: string;
    fontSize?: string | number;
    isPassword?: boolean;
}

export const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
    <Typography color="error" variant="caption">
        {message}
    </Typography>
);

export const StyledTextField = styled(TextField)<{ $fontSize?: string | number }>(({ theme, $fontSize }) => ({

    "& .MuiOutlinedInput-root": {
        backgroundColor: theme.palette.greyColors.shadow,
        borderRadius: "16px",
        height: "52px",
        overflow: "hidden",
        "& fieldset": {
            border: "none",
        },
        "&:hover fieldset": {
            border: "none",
        },
        "&.Mui-focused fieldset": {
            border: "none",
        },
        "& input": {
            color: theme.palette.blueColors.main,
            fontFamily: "Nunito Sans 500",
            fontSize: $fontSize || '16px',
            "&:-webkit-autofill": {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.greyColors.shadow} inset !important`,
                WebkitTextFillColor: `${theme.palette.blueColors.main} !important`,
                caretColor: `${theme.palette.blueColors.main} !important`,
                borderRadius: "0 !important",
                "&::first-line": {
                    fontSize: $fontSize || '16px',
                },
            },
            "&[type='password']": {
                fontFamily: "Nunito Sans 500",
                fontSize: $fontSize || '16px',
            },
        },
        "& .MuiInputAdornment-root": {
            marginLeft: 0,
            marginRight: 0,
            height: "100%",
            maxHeight: "none",
        },
        "& .MuiInputAdornment-root .MuiSvgIcon-root": {
            color: theme.palette.blueColors.main,
        },
    },
    "& .MuiInputLabel-root": {
        color: theme.palette.blueColors.main,
        fontFamily: "Nunito Sans 500",
        fontSize: $fontSize || '16px',
    },
    "& .MuiFormHelperText-root": {
        fontSize: $fontSize || '18px',
        fontFamily: "Nunito Sans 500",
    },
}));

const CommonTextField: React.FC<CommonTextFieldProps> = ({
    name,
    icon,
    error,
    helperText,
    fontSize = '16px',
    isPassword = false,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme()
    const iconColor = theme.palette.primary.main;
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <StyledTextField
            name={name}
            placeholder={t(`${name}.placeholder`)}
            fullWidth
            error={error}
            helperText={helperText}
            $fontSize={fontSize}
            type={isPassword ? (showPassword ? 'text' : 'password') : props.type}
            slotProps={{
                input: {
                    startAdornment: icon && (
                        <InputAdornment position="start" sx={{ paddingLeft: '12px', paddingRight: '8px' }}>
                            <img
                                src={icon}
                                alt={`${name} icon`}
                                width="18px"
                                height="21px"
                            />
                        </InputAdornment>
                    ),
                    endAdornment: isPassword && (
                        <InputAdornment position="end" sx={{ paddingLeft: '6px', paddingRight: '16px' }}>
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {!showPassword ?
                                    <EyeClosed style={{ color: iconColor, width: 29, height: 29 }} />
                                    :
                                    <EyeNormal style={{ color: iconColor, width: 29, height: 29 }} />
                                }
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
            onChange={(e) => {
                if (props.onChange) {
                    props.onChange(e);
                }
            }}
            {...props}
        />
    );
};

export default CommonTextField;